/**
 * URL routing for the application shell — a parse table, not a framework.
 *
 * Four addresses, one per screen the shell can be asked for from outside:
 * `/start` (the title), `/settings`, `/play` (the run), `/workshop` (the
 * in-game editors), plus `/workshop/<kind>/<name>` for a specific document.
 * The prologue and the run-setup screen deliberately have no address — both
 * are steps *inside* an intent, not places to arrive: a deep link into setup
 * would skip the once-per-profile prologue check, and a link into the
 * prologue would replay a scene whose whole design is that it happens once.
 * Anything unrecognised is `unknown`, and the shell answers that with
 * `/start` rather than a 404 the static host cannot serve anyway.
 *
 * History-API paths rather than `#/` fragments, because every way this app
 * is served already falls back to the index: `vite dev` and `vite preview`
 * are SPA-by-default, and the deployed build is `serve -s dist` (Procfile),
 * where `-s` is exactly that rewrite. Hash routing is the answer to a host
 * constraint this project does not have.
 *
 * Pure parsing lives in `parseRoute` and the two workshop mappers so tests
 * can hold them without a DOM; the two functions that touch `window` are the
 * only ones that do, and the popstate listener hands its caller a parsed
 * intent rather than a URL, so the shell never reads `location` twice and
 * disagrees with itself.
 *
 * **The kind/name → file mapping is a registry lookup, not a directory
 * rule.** `/workshop/tile/cistern` could be spelled as a path template, but
 * a template is the hand copy (docs/30 §2.3): the moment an asset moves or a
 * fifth kind arrives, the URL scheme silently diverges from what the picker
 * offers. Both mappers take the same `assetList` the editor sessions are
 * built from, so a link resolves exactly when the build holds the document —
 * the same membership test `openGameSession` applies one step later.
 */

export type WorkshopKind = 'tile' | 'mask' | 'plan' | 'motion' | 'worldmap';

/** A workshop document as the URL spells it: `/workshop/<kind>/<name>`. */
export interface WorkshopDoc {
  kind: WorkshopKind;
  name: string;
}

export type RouteIntent =
  | { kind: 'start' }
  | { kind: 'settings' }
  | { kind: 'play' }
  | { kind: 'workshop'; doc?: WorkshopDoc }
  /** Not on the table. The shell redirects this to `/start`. */
  | { kind: 'unknown' };

/**
 * The editors reachable by a `/workshop/<kind>/<name>` URL. Keep in sync with
 * `WorkshopKind`. Not every `EDITOR_KINDS` entry is here — `weapon` (#168)
 * shipped without a deep link, a pre-existing gap this list does not close;
 * `worldmap` (#182) is added because it is one document (`worldmap`, always),
 * so `/workshop/worldmap/worldmap` is a real link and not a template guessing
 * at a name that might not exist.
 */
const WORKSHOP_KINDS: readonly string[] = ['tile', 'mask', 'plan', 'motion', 'worldmap'];

/**
 * One pathname → one intent. Pure, and total: every input maps somewhere,
 * with `unknown` as the explicit "not on the table" answer rather than null —
 * the caller has to handle it, and handling it is where the `/start`
 * redirect lives.
 */
export function parseRoute(pathname: string): RouteIntent {
  // Split-and-filter rather than string equality so `/start/`, `//start` and
  // `/start` are one spelling — trailing slashes are what humans type.
  const segs = pathname.split('/').filter((s) => s.length > 0);
  if (segs.length === 0) return { kind: 'start' };
  if (segs.length === 1) {
    if (segs[0] === 'start') return { kind: 'start' };
    if (segs[0] === 'settings') return { kind: 'settings' };
    if (segs[0] === 'play') return { kind: 'play' };
    if (segs[0] === 'workshop') return { kind: 'workshop' };
    return { kind: 'unknown' };
  }
  if (segs[0] === 'workshop' && segs.length === 3 && WORKSHOP_KINDS.includes(segs[1])) {
    return { kind: 'workshop', doc: { kind: segs[1] as WorkshopKind, name: segs[2] } };
  }
  return { kind: 'unknown' };
}

/** What the workshop mappers need of an asset — `assetList`'s own fields. */
export interface RoutableAsset {
  kind: string;
  name: string;
  path: string;
}

/**
 * `/workshop/<kind>/<name>` → the repo-relative path `EditorHost` opens, or
 * `undefined` when the build holds no such document — which the shell treats
 * as an unknown route, because a link to a document that is not there *is*
 * one.
 */
export function workshopDocPath(
  doc: WorkshopDoc,
  assets: readonly RoutableAsset[],
): string | undefined {
  return assets.find((a) => a.kind === doc.kind && a.name === doc.name)?.path;
}

/**
 * The inverse: the URL for a document the workshop just opened. Falls back
 * to plain `/workshop` for a path the registry does not hold — a fresh
 * `/new` document or an upload exists only in the page's memory, so an
 * address claiming otherwise would be a link that breaks on refresh.
 */
export function workshopRouteFor(path: string, assets: readonly RoutableAsset[]): string {
  const a = assets.find((x) => x.path === path);
  if (a === undefined || !WORKSHOP_KINDS.includes(a.kind)) return '/workshop';
  return `/workshop/${a.kind}/${a.name}`;
}

/**
 * Move the address bar. `replace` for corrections (canonicalising `/`,
 * bouncing an unknown path, tracking `/open` inside the workshop) so they
 * never become Back-button stops; push for real navigation. A no-op when
 * the URL is already there, which is what lets the shell sync the route on
 * every scene entry without guarding against the popstate that caused it —
 * by the time the handler runs, the URL is already the target.
 */
export function setRoute(path: string, replace = false): void {
  if (window.location.pathname === path) return;
  if (replace) window.history.replaceState(null, '', path);
  else window.history.pushState(null, '', path);
}

/** Back/forward. The handler gets the parsed intent, never the raw URL. */
export function installRouter(handler: (intent: RouteIntent) => void): void {
  window.addEventListener('popstate', () => handler(parseRoute(window.location.pathname)));
}
